/** Runtime settings for the server-rendered venue page. */
export type VenueSsrConfig = {
  timeoutMs: number;
  sMaxAge: number;
  staleWhileRevalidate: number;
};

const VENUE_SSR_DEFAULTS: VenueSsrConfig = {
  timeoutMs: 5000,
  sMaxAge: 300,
  staleWhileRevalidate: 86400,
};

export const NO_STORE = "no-store";

function positiveInteger(value: string | undefined, fallback: number): number {
  if (!value || !/^\d+$/.test(value.trim())) return fallback;
  const parsed = Number(value);
  return parsed > 0 ? parsed : fallback;
}

/**
 * Reads the venue SSR settings from the server's environment.
 *
 * @param env The process environment, or `{}` outside Node.
 * @returns The settings, each falling back to its default unless a positive integer.
 */
export function readVenueSsrConfig(
  env: Record<string, string | undefined>,
): VenueSsrConfig {
  return {
    timeoutMs: positiveInteger(
      env.VENUE_SSR_FETCH_TIMEOUT_MS,
      VENUE_SSR_DEFAULTS.timeoutMs,
    ),
    sMaxAge: positiveInteger(
      env.VENUE_PAGE_CACHE_S_MAXAGE,
      VENUE_SSR_DEFAULTS.sMaxAge,
    ),
    staleWhileRevalidate: positiveInteger(
      env.VENUE_PAGE_CACHE_SWR,
      VENUE_SSR_DEFAULTS.staleWhileRevalidate,
    ),
  };
}

/**
 * The shared-cache header for a venue page that rendered normally.
 *
 * @param config The venue SSR settings.
 * @returns A `Cache-Control` value.
 */
export function cacheableHeader(config: VenueSsrConfig): string {
  return `public, s-maxage=${config.sMaxAge}, stale-while-revalidate=${config.staleWhileRevalidate}`;
}

export class VenueFetchTimeoutError extends Error {
  constructor(ms: number) {
    super(`Venue fetch exceeded ${ms} ms`);
    this.name = "VenueFetchTimeoutError";
  }
}

/**
 * Rejects with VenueFetchTimeoutError once `ms` passes, calling `onTimeout` first.
 *
 * @param promise The work to wait for.
 * @param ms The time limit.
 * @param onTimeout Called once when the limit is hit, e.g. to abort the work.
 * @returns The work's result, if it settles in time.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  onTimeout: () => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout();
      reject(new VenueFetchTimeoutError(ms));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Marks any non-200 venue page response `no-store`.
 *
 * @param url The request URL.
 * @param response The rendered response.
 * @returns The response, with `Cache-Control: no-store` when it is a non-200 venue page.
 */
export function withVenueNoStore(url: string, response: Response): Response {
  if (response.status === 200) return response;

  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return response;
  }
  // The router matches paths case-insensitively, so /VenueDetails/x is a venue page too.
  if (!pathname.toLowerCase().startsWith("/venuedetails/")) return response;

  // h3 drops event headers on non-2xx and redirects bypass the stream callback, so
  // this is the one place every venue 404 / 410 / 301 / 500 passes through.
  const headers = new Headers(response.headers);
  headers.set("Cache-Control", NO_STORE);
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}
